import { ReactNode } from 'react';
import { Button, ButtonProps, SxProps, Theme } from '@mui/material';
import { colors } from '../../ui/colors';

type PrimaryButtonVariant = 'contained' | 'outlined';

interface PrimaryButtonProps extends Omit<ButtonProps, 'variant'> {
    children: ReactNode;
    variant?: PrimaryButtonVariant;
    sx?: SxProps<Theme>;
}

export const PrimaryButton = ({
    children,
    variant = 'contained',
    fullWidth = false,
    size = 'small',
    sx,
    ...props
}: PrimaryButtonProps) => {
    const isOutlined = variant === 'outlined';

    return (
        <Button
            fullWidth={fullWidth}
            size={size}
            {...props}
            sx={[
                {
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: 11, sm: 12, md: 13 },
                    py: 1,
                    px: 0.5,
                    minWidth: 0,
                    boxShadow: 'none',
                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',

                    bgcolor: isOutlined ? 'transparent' : colors.orange.base,
                    color: isOutlined ? colors.orange.base : 'white',
                    border: `1px solid ${colors.orange.base}`,

                    '&:hover': {
                        bgcolor: isOutlined
                            ? 'rgba(255, 107, 0, 0.08)'
                            : colors.orange.base,
                        boxShadow: isOutlined
                            ? 'none'
                            : '0 2px 8px rgba(255, 107, 0, 0.18)',
                        filter: isOutlined ? 'none' : 'brightness(1.03)',
                        borderColor: colors.orange.base,
                    },

                    '&:active': {
                        boxShadow: 'none',
                        filter: isOutlined ? 'none' : 'brightness(0.98)',
                    },

                    '&.Mui-disabled': {
                        bgcolor: isOutlined ? 'transparent' : 'rgba(255, 107, 0, 0.45)',
                        color: isOutlined
                            ? 'rgba(255, 107, 0, 0.45)'
                            : 'rgba(255,255,255,0.7)',
                        borderColor: 'rgba(255, 107, 0, 0.35)',
                        boxShadow: 'none',
                        filter: 'none',
                    },
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {children}
        </Button>
    );
};